import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getCompaniesWithRoles } from "../utilities/userCompanyRoles";
import { USER_UPDATED_EVENT } from "../utilities/currentUser";

const CompanyContext = createContext(null);
const STORAGE_KEY = "last_company_id";

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

/**
 * The company to start in: the saved one if the user still belongs to it, else their first company.
 * It is written to localStorage right away, because the axios instance reads `last_company_id` to send the
 * X-Company-Id header. Without this write, nothing is sent until the user clicks a company, and the first
 * requests after login/refresh go out without the header.
 */
function resolveInitialCompanyId(user) {
  const list = getCompaniesWithRoles(user);
  if (list.length === 0) return null;
  const saved = Number(localStorage.getItem(STORAGE_KEY));
  const id =
    saved && list.some((c) => c.company.id === saved) ? saved : list[0].company.id;
  try {
    localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    // Storage can be blocked (private mode); the in-memory selection still works.
  }
  return id;
}

export function CompanyProvider({ children, user: userProp }) {
  const [user, setUser] = useState(userProp || readUser());

  useEffect(() => {
    if (userProp) setUser(userProp);
  }, [userProp]);

  useEffect(() => {
    const onStorage = () => {
      const stored = readUser();
      // A user who just logged in has no saved company yet: persist the first one before pages fetch.
      resolveInitialCompanyId(stored);
      setUser(stored);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(USER_UPDATED_EVENT, onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(USER_UPDATED_EVENT, onStorage);
    };
  }, []);

  const companiesWithRoles = useMemo(
    () => getCompaniesWithRoles(user),
    [user]
  );

  const [currentCompanyId, setCurrentCompanyIdState] = useState(() =>
    resolveInitialCompanyId(readUser())
  );

  useEffect(() => {
    if (companiesWithRoles.length === 0) {
      setCurrentCompanyIdState(null);
      return;
    }
    const stillValid = companiesWithRoles.some(
      (c) => c.company.id === currentCompanyId
    );
    if (!stillValid) {
      const saved = Number(localStorage.getItem(STORAGE_KEY));
      const next =
        companiesWithRoles.find((c) => c.company.id === saved)?.company.id ||
        companiesWithRoles[0].company.id;
      setCurrentCompanyIdState(next);
      localStorage.setItem(STORAGE_KEY, String(next));
    }
  }, [companiesWithRoles, currentCompanyId]);

  const setCurrentCompanyId = useCallback((id) => {
    setCurrentCompanyIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const currentCompany = useMemo(
    () =>
      companiesWithRoles.find((c) => c.company.id === currentCompanyId) || null,
    [companiesWithRoles, currentCompanyId]
  );

  const value = useMemo(
    () => ({
      user,
      setUser,
      companiesWithRoles,
      currentCompanyId,
      setCurrentCompanyId,
      currentCompany,
      rolesInCurrentCompany: currentCompany?.roles || [],
    }),
    [
      user,
      companiesWithRoles,
      currentCompanyId,
      setCurrentCompanyId,
      currentCompany,
    ]
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    throw new Error("useCompany must be used within CompanyProvider");
  }
  return ctx;
}
